import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Button, HStack, Popover, Text, Portal, Separator, Checkbox, FileUpload, ColorPicker, parseColor, NumberInput, Field, Slider, Collapsible, Box, VStack } from "@chakra-ui/react";
import { useAppStore } from "../../../context/store";
import { LuPlus, LuRotate3D, LuX } from "react-icons/lu";
import { Tooltip } from "../../ui/tooltip";
import { Cartesian3, Cesium3DTileStyle, Color, Matrix4, PointCloudShading, Transforms, Math as CesiumMath, CustomShader, Cartographic } from "cesium";
import Type from "../../../utilies/Types";
import { rgbaToHex } from "../../../utilies/Utility";
import { addImageBoards } from "../../../utilies/PointCloudUtility";
import { useMapObjects } from "../../../context/MapObjectsContext";
function PointCloud() {
  // state
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const isOpenPointCloudPanel = useAppStore((s) => s.isOpenPointCloudPanel);
  const viewerRef = useAppStore((s) => s.viewerRef);
  const cesiumContainer = useAppStore((s) => s.cesiumContainer);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const bgImageUrl = useAppStore((s) => s.bgImageUrl);
  const imageGeojson = useAppStore((s) => s.imageGeojson);
  const camera = useAppStore((s) => s.camera);
  const pointCloudStyle = useAppStore((s) => s.pointCloudStyle);

  // setters
  const setIsOpenPointCloudPanel = useAppStore.getState().setIsOpenPointCloudPanel;
  const setBgImageUrl = useAppStore.getState().setBgImageUrl;
  const setPointCloudStyle = useAppStore.getState().setPointCloudStyle;

  const delayToUpdate = 500;
  const debounceTimerRef = useRef(null);

  // tileset & caches
  const tilesetRef = useRef(null);
  const lastStyleStrRef = useRef("");
  const shaderCacheRef = useRef({ circle: null });
  const initialModelMatrixRef = useRef(null);
  const lastTilesetSeenRef = useRef(null);
  const lastAppliedUrlRef = useRef(null);

  // UI state
  const [bgOffset, setBgOffset] = useState({ x: 0, y: 0 });
  const [pointCloudMode, setPointCloudMode] = useState("map");
  const pointCloudModeRef = useRef(pointCloudMode);
  useEffect(() => {
    pointCloudModeRef.current = pointCloudMode;
  }, [pointCloudMode]);

  const [heightOffset, setHeightOffset] = useState(0);
  const [pointSize, setPointSize] = useState(5);
  const lastPointSizeRef = useRef(5);
  useEffect(() => {
    lastPointSizeRef.current = pointSize;
  }, [pointSize]);

  const [isPointSizeDynamic, setIsPointSizeDynamic] = useState(false);
  const isPointSizeDynamicRef = useRef(isPointSizeDynamic);
  useEffect(() => {
    isPointSizeDynamicRef.current = isPointSizeDynamic;
  }, [isPointSizeDynamic]);

  const [pointColor, setPointsColor] = useState("#ffffff");
  const [pointShape, setPointShape] = useState("circle");

  const [returnNumberWeight, setReturnNumberWeight] = useState(0);
  const [returnNumberVisibleRange, setReturnNumberVisibleRange] = useState([1, 5]);

  const [bit, setBit] = useState(16);
  const [isActiveIntensity, setIsActiveIntensity] = useState(true);
  const [intensityRange, setIntensityRange] = useState([0, 65535]);
  const [intensityColor, setIntensityColor] = useState({ from: "#000000", to: "#ffffff" });

  const [isActiveRGB, setIsActiveRGB] = useState(true);
  const [rgbData, setRgbData] = useState({ r: 0, g: 0, b: 0 });

  const [isActiveClassification, setIsActiveClassification] = useState(false);
  const [classificationData, setClassificationData] = useState({
    ground: "#8B4513",
    low_vegetation: "#90EE90",
    medium_vegetation: "#008000",
    high_vegetation: "#006400",
    building: "#808080",
    water: "#0000FF",
    noise: "#FF0000",
    road_surface: "#FFA500",
    other: "#FFFFFF",
  });

  // cache tileset when mapObjects changes
  useEffect(() => {
    let found = null;
    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (obj && obj.type === Type.ASSET.THREEDTILES) {
        found = obj.tileset;
        break;
      }
    }
    tilesetRef.current = found || null;
    // no cleanup function
  }, [mapObjects]);

  const lastSavedStyleRef = useRef("");

  // build a stable settings object
  const buildPointCloudStyle = useCallback(() => {
    // collect all UI settings in one place
    return {
      // rendering
      pointSize,
      isPointSizeDynamic,
      pointShape,
      pointColor,

      // intensity
      isActiveIntensity,
      bit,
      intensityRange,
      intensityColor,

      // RGB
      isActiveRGB,
      rgbData,

      // classification
      isActiveClassification,
      classificationData,

      // return number
      returnNumberWeight,
      returnNumberVisibleRange,

      // mode and background
      pointCloudMode,
      bgImageUrl,
      bgOffset,

      // transforms
      heightOffset,
    };
  }, [
    pointSize,
    isPointSizeDynamic,
    pointShape,
    pointColor,
    isActiveIntensity,
    bit,
    intensityRange,
    intensityColor,
    isActiveRGB,
    rgbData,
    isActiveClassification,
    classificationData,
    returnNumberWeight,
    returnNumberVisibleRange,
    pointCloudMode,
    bgImageUrl,
    bgOffset,
    heightOffset,
  ]);

  // sync settings to context object without extra renders
  useEffect(() => {
    // stringify for cheap change detection
    const styleObj = buildPointCloudStyle();
    const sig = JSON.stringify(styleObj);
    if (lastSavedStyleRef.current !== sig) {
      setPointCloudStyle(styleObj); // store in context
      lastSavedStyleRef.current = sig; // cache signature
    }
  }, [buildPointCloudStyle, setPointCloudStyle]);

  // style signature builder
  const buildStyleString = useCallback(() => {
    return JSON.stringify({
      pointSize,
      pointColor,
      isActiveIntensity,
      intensityRange,
      intensityColor,
      isActiveRGB,
      rgbData,
      isActiveClassification,
      classificationData,
      pointShape,
      returnNumberVisibleRange,
      returnNumberWeight,
    });
  }, [
    pointSize,
    pointColor,
    isActiveIntensity,
    intensityRange,
    intensityColor,
    isActiveRGB,
    rgbData,
    isActiveClassification,
    classificationData,
    pointShape,
    returnNumberVisibleRange,
    returnNumberWeight,
  ]);

  // hydrate state from saved pointCloudStyle on mount
  useEffect(() => {
    const s = pointCloudStyle || null;
    if (!s) return; // nothing saved

    // rendering
    setPointSize(s.pointSize ?? 5); // number
    setIsPointSizeDynamic(!!s.isPointSizeDynamic); // boolean
    setPointShape(s.pointShape ?? "circle"); // "circle" | "square"
    setPointsColor(s.pointColor ?? "#ffffff"); // hex

    // intensity
    setIsActiveIntensity(s.isActiveIntensity ?? true);
    setBit(s.bit ?? 16);
    setIntensityRange(s.intensityRange ?? [0, 65535]);
    setIntensityColor(s.intensityColor ?? { from: "#000000", to: "#ffffff" });

    // RGB
    setIsActiveRGB(s.isActiveRGB ?? true);
    setRgbData(s.rgbData ?? { r: 0, g: 0, b: 0 });

    // classification
    setIsActiveClassification(s.isActiveClassification ?? false);
    setClassificationData(
      s.classificationData ?? {
        ground: "#8B4513",
        low_vegetation: "#90EE90",
        medium_vegetation: "#008000",
        high_vegetation: "#006400",
        building: "#808080",
        water: "#0000FF",
        noise: "#FF0000",
        road_surface: "#FFA500",
        other: "#FFFFFF",
      },
    );

    // return number
    setReturnNumberWeight(s.returnNumberWeight ?? 0);
    setReturnNumberVisibleRange(s.returnNumberVisibleRange ?? [1, 5]);

    // mode and background
    setPointCloudMode(s.pointCloudMode ?? "map");
    if (s.bgImageUrl) setBgImageUrl(s.bgImageUrl);
    setBgOffset(s.bgOffset ?? { x: 0, y: 0 });

    // transforms
    setHeightOffset(s.heightOffset ?? 0);
  }, []); // run once on mount

  // shader cache
  const ensureShaders = useCallback(() => {
    if (!shaderCacheRef.current.circle) {
      shaderCacheRef.current.circle = new CustomShader({
        fragmentShaderText: `
          void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
            vec2 coords = gl_PointCoord * 2.0 - 1.0;
            if (dot(coords, coords) > 1.0) {
              discard;
            }
          }
        `,
      });
    }
  }, []);

  // apply tileset style when relevant props change
  useEffect(() => {
    const ts = tilesetRef.current;
    if (!ts) return;

    ensureShaders();

    // shader apply minimal
    if (pointShape === "circle") {
      if (ts.customShader !== shaderCacheRef.current.circle) {
        ts.customShader = shaderCacheRef.current.circle;
      }
    } else {
      if (ts.customShader) ts.customShader = undefined;
    }

    // build expressions
    let colorExpr = `color('${pointColor}')`;
    let showExpr = "true";

    if (isActiveClassification) {
      colorExpr = `(
       (${`$\{Classification\}`} === 2) ? color('${classificationData.ground}') :
       (${`$\{Classification\}`} === 3) ? color('${classificationData.low_vegetation}') :
       (${`$\{Classification\}`} === 4) ? color('${classificationData.medium_vegetation}') :
       (${`$\{Classification\}`} === 5) ? color('${classificationData.high_vegetation}') :
       (${`$\{Classification\}`} === 6) ? color('${classificationData.building}') :
       (${`$\{Classification\}`} === 9) ? color('${classificationData.water}') :
       (${`$\{Classification\}`} === 7) ? color('${classificationData.noise}') :
       (${`$\{Classification\}`} === 11) ? color('${classificationData.road_surface}') :
       color('${classificationData.other}')
      )`;
    } else if (isActiveRGB) {
      const r = "${Red}";
      const g = "${Green}";
      const b = "${Blue}";
      showExpr = `(${r} >= ${rgbData.r}) && (${g} >= ${rgbData.g}) && (${b} >= ${rgbData.b})`;
      colorExpr = undefined; // use RGB colors from tileset
    } else if (isActiveIntensity) {
      const prop = "${intensity}";
      const denom = Math.max(1e-9, intensityRange[1] - intensityRange[0]);
      const norm = `clamp((${prop} - ${intensityRange[0]}) / ${denom}, 0.0, 1.0)`;
      colorExpr = `mix(color('${intensityColor.from}'), color('${intensityColor.to}'), ${norm})`;
      showExpr = `(${prop} >= ${intensityRange[0]}) && (${prop} <= ${intensityRange[1]})`;
    } else {
      const prop = "${ReturnNumber}";
      showExpr = `(${returnNumberVisibleRange[0]} <= ${prop} && ${prop} <= ${returnNumberVisibleRange[1]})`;
      const denom = Math.max(1e-9, returnNumberVisibleRange[1] - returnNumberVisibleRange[0]);
      const norm = `clamp((${prop} - ${returnNumberVisibleRange[0]}) / ${denom}, 0.0, 1.0)`;
      const weighted = `clamp(${returnNumberWeight / 100} * ${norm}, 0.0, 1.0)`;
      colorExpr = `mix(color('blue'), color('red'), ${weighted})`;
    }

    const styleOptions = { show: showExpr, pointSize };
    if (colorExpr !== undefined) styleOptions.color = colorExpr;

    const styleStr = buildStyleString();
    if (lastStyleStrRef.current !== styleStr) {
      try {
        ts.style = new Cesium3DTileStyle(styleOptions);
        lastStyleStrRef.current = styleStr;
      } catch (err) {
        console.warn("style apply failed", err);
      }
    }

    if (ts.pointCloudShading instanceof PointCloudShading) {
      ts.pointCloudShading.attenuation = false;
      ts.pointCloudShading.maximumAttenuation = undefined;
      ts.pointCloudShading.baseResolution = undefined;
    }
    // no cleanup
  }, [
    pointSize,
    pointColor,
    isActiveIntensity,
    intensityRange,
    intensityColor,
    isActiveRGB,
    rgbData,
    isActiveClassification,
    classificationData,
    pointShape,
    returnNumberVisibleRange,
    returnNumberWeight,
    buildStyleString,
    ensureShaders,
  ]);

  // dynamic size using requestAnimationFrame; cleanup cancels RAF
  useEffect(() => {
    const viewer = viewerRef?.current;
    if (!viewer) return;

    let rafId = null;
    const tick = () => {
      if (!isPointSizeDynamicRef.current) {
        setPointSize(lastPointSizeRef.current);
        return;
      }
      const cam = viewer.camera;
      const height = (cam.positionCartographic && cam.positionCartographic.height) || Cartographic.fromCartesian(cam.position).height || 1000;
      let size = 1 + Math.max(0, (2000 - Math.min(height, 2000)) / 2000) * 7;
      size = Math.min(Math.max(size, 0.5), 12);
      if (Math.abs(size - pointSize) > 0.2) setPointSize(parseFloat(size.toFixed(2)));
      rafId = requestAnimationFrame(tick);
    };

    if (isPointSizeDynamicRef.current) rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [viewerRef, isPointSizeDynamic, pointSize]);

  // background helpers
  const getBackgroundPositionCss = useCallback(() => `calc(50% + ${bgOffset.x}px) calc(50% + ${bgOffset.y}px)`, [bgOffset]);

  const applyImageBackground = useCallback(
    (url) => {
      const containerEl = cesiumContainer?.current || null;
      if (!containerEl) return;
      if (url) {
        containerEl.style.backgroundImage = `url(${url})`;
        containerEl.style.backgroundRepeat = "no-repeat";
        containerEl.style.backgroundPosition = getBackgroundPositionCss();
        containerEl.style.backgroundSize = "cover";
        containerEl.style.backgroundColor = "transparent";
      } else {
        containerEl.style.backgroundImage = "";
        containerEl.style.backgroundRepeat = "";
        containerEl.style.backgroundPosition = "";
        containerEl.style.backgroundSize = "";
        containerEl.style.backgroundColor = "";
      }
      lastAppliedUrlRef.current = url;
    },
    [cesiumContainer, getBackgroundPositionCss],
  );

  // mode init functions
  const initModeMap = useCallback(() => {
    const viewer = viewerRef?.current;
    if (!viewer) return;
    const scene = viewer.scene;
    scene.skyBox.show = true;
    scene.skyAtmosphere.show = true;
    scene.globe.show = true;
    scene.backgroundColor = Color.BLACK;
    applyImageBackground(null);
  }, [viewerRef, applyImageBackground]);

  const initModeObject = useCallback(() => {
    const viewer = viewerRef?.current;
    if (!viewer) return;
    const scene = viewer.scene;
    scene.skyBox.show = false;
    scene.skyAtmosphere.show = false;
    scene.globe.show = false;
    scene.backgroundColor = Color.TRANSPARENT;
    if (bgImageUrl) applyImageBackground(bgImageUrl);
  }, [viewerRef, applyImageBackground, bgImageUrl]);

  // apply mode change
  useEffect(() => {
    if (!viewerRef?.current) return;
    if (pointCloudMode === "map") initModeMap();
    else initModeObject();
    // no cleanup
  }, [pointCloudMode, bgImageUrl, initModeMap, initModeObject]);

  // reapply background on changes
  useEffect(() => {
    if (!viewerRef?.current) return;
    if (pointCloudMode === "object") {
      viewerRef.current.scene.backgroundColor = Color.TRANSPARENT;
      applyImageBackground(bgImageUrl);
    } else {
      applyImageBackground(null);
    }
    // no cleanup
  }, [bgImageUrl, pointCloudMode, bgOffset, applyImageBackground, viewerRef]);

  // move tileset in local up direction (non-accumulative)
  const movePointCloudZRelative = useCallback((tileset, offsetMeters) => {
    if (!tileset || typeof offsetMeters !== "number") return;
    if (lastTilesetSeenRef.current !== tileset) {
      initialModelMatrixRef.current = Matrix4.clone(tileset.modelMatrix);
      lastTilesetSeenRef.current = tileset;
    }
    if (!initialModelMatrixRef.current) initialModelMatrixRef.current = Matrix4.clone(tileset.modelMatrix);
    const center = tileset.boundingSphere?.center || Cartesian3.ZERO;
    const enu = Transforms.eastNorthUpToFixedFrame(center);
    const up = new Cartesian3();
    Matrix4.getColumn(enu, 2, up);
    const upDir = Cartesian3.normalize(up, new Cartesian3());
    const delta = Cartesian3.multiplyByScalar(upDir, offsetMeters, new Cartesian3());
    const result = Matrix4.multiplyByTranslation(initialModelMatrixRef.current, delta, new Matrix4());
    tileset.modelMatrix = result;
  }, []);

  // keyboard handling, safe cleanup
  // keyboard handling using savedHotkeysRef
  useEffect(() => {
    const viewer = viewerRef?.current;
    if (!viewer) return;

    const normalizeKey = (k) => {
      if (!k) return "";
      let key = k.toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const onKeyDown = (event) => {
      const pressed = [];
      if (event.ctrlKey) pressed.push("ctrl");
      if (event.shiftKey) pressed.push("shift");
      if (event.altKey) pressed.push("alt");
      if (event.metaKey) pressed.push("meta");
      pressed.push(normalizeKey(event.key));
      const combo = pressed.join("+");

      const hotkeys = savedHotkeysRef?.current || [];
      const match = hotkeys.find((hk) => hk && hk.combination.join("+").toLowerCase() === combo);
      if (!match) return;

      const cam = viewer.camera;
      const scene = viewer.scene;
      const ts = tilesetRef.current;
      const zoomStep = 0.1;
      const moveStep = 2.0;
      const angleStep = CesiumMath.toRadians(0.2);

      switch (match.id) {
        // background offset
        case "bg-move-up":
          setBgOffset((p) => ({ ...p, y: p.y - 10 }));
          break;
        case "bg-move-down":
          setBgOffset((p) => ({ ...p, y: p.y + 10 }));
          break;
        case "bg-move-left":
          setBgOffset((p) => ({ ...p, x: p.x - 10 }));
          break;
        case "bg-move-right":
          setBgOffset((p) => ({ ...p, x: p.x + 10 }));
          break;

        // zoom
        case "zoom-in":
          cam.moveForward(zoomStep);
          break;
        case "zoom-out":
          cam.moveBackward(zoomStep);
          break;

        // movement
        case "move-forward":
          cam.move(Cartesian3.multiplyByScalar(cam.direction, moveStep, new Cartesian3()), 1.0);
          break;
        case "move-backward":
          cam.move(Cartesian3.multiplyByScalar(cam.direction, -moveStep, new Cartesian3()), 1.0);
          break;
        case "move-left":
          cam.move(Cartesian3.multiplyByScalar(cam.right, -0.1, new Cartesian3()), 1.0);
          break;
        case "move-right":
          cam.move(Cartesian3.multiplyByScalar(cam.right, 0.1, new Cartesian3()), 1.0);
          break;

        // height
        case "height-up": {
          const pos = Cartographic.fromCartesian(cam.position);
          pos.height += 1.0;
          cam.setView({ destination: Cartesian3.fromRadians(pos.longitude, pos.latitude, pos.height) });
          break;
        }
        case "height-down": {
          const pos = Cartographic.fromCartesian(cam.position);
          pos.height = Math.max(0.1, pos.height - 1.0);
          cam.setView({ destination: Cartesian3.fromRadians(pos.longitude, pos.latitude, pos.height) });
          break;
        }

        // rotation
        case "rotate-up":
          cam.setView({ orientation: { heading: cam.heading, pitch: cam.pitch + angleStep, roll: cam.roll } });
          break;
        case "rotate-down":
          cam.setView({ orientation: { heading: cam.heading, pitch: cam.pitch - angleStep, roll: cam.roll } });
          break;
        case "rotate-left":
          cam.setView({ orientation: { heading: cam.heading - angleStep, pitch: cam.pitch, roll: cam.roll } });
          break;
        case "rotate-right":
          cam.setView({ orientation: { heading: cam.heading + angleStep, pitch: cam.pitch, roll: cam.roll } });
          break;

        default:
          return;
      }

      event.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [viewerRef, savedHotkeysRef]);

  // revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (bgImageUrl && bgImageUrl.startsWith && bgImageUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(bgImageUrl);
        } catch (_) {
          /* ignore */
        }
      }
    };
  }, [bgImageUrl]);

  // reset offset on map mode
  useEffect(() => {
    if (pointCloudMode === "map") setBgOffset({ x: 0, y: 0 });
  }, [pointCloudMode]);

  // if bg image is provided programmatically switch to object mode
  useEffect(() => {
    if (bgImageUrl) setPointCloudMode("object");
  }, [bgImageUrl]);

  // billboards handler with safe destroy checks
  const [billboardsHandler, setBillboardsHandler] = useState(null);
  useEffect(() => {
    if (!viewerRef?.current) {
      if (billboardsHandler && typeof billboardsHandler.destroy === "function") billboardsHandler.destroy();
      setBillboardsHandler(null);
      return;
    }

    if (!imageGeojson || !camera) {
      if (billboardsHandler && typeof billboardsHandler.destroy === "function") billboardsHandler.destroy();
      setBillboardsHandler(null);
      return;
    }

    const temp = [];
    const features = imageGeojson.features || [];
    for (let i = 0; i < features.length; i++) {
      const props = features[i].properties || {};
      const imgs = props.images || {};
      for (const k in imgs) {
        const im = imgs[k];
        if (im.camera_type == camera) {
          temp.push({
            path: im.relpath,
            azimuth: props.azimuth,
            coordinate: features[i].geometry.coordinates,
          });
        }
      }
    }

    if (billboardsHandler && typeof billboardsHandler.destroy === "function") {
      billboardsHandler.destroy();
    }

    const boards = addImageBoards(viewerRef.current, temp, { width: 25, height: 15 }, applyImageBackground);
    setBillboardsHandler(boards);

    return () => {
      if (boards && typeof boards.destroy === "function") boards.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageGeojson, camera, viewerRef, applyImageBackground]);

  // debounce helper
  const debounceSet = (fn, val) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fn(val), delayToUpdate);
  };

  const getTileset = useCallback(() => tilesetRef.current, []);

  // render UI
  return (
    <Popover.Root positioning={{ placement: "left" }} open={isOpenPointCloudPanel} onOpenChange={(e) => setIsOpenPointCloudPanel(e.open)} closeOnInteractOutside={false}>
      <Popover.Trigger asChild>
        <Button userSelect={"none"} p={0} bg={"Background"}>
          <Tooltip content="Point Cloud Setting">
            <LuRotate3D color="MenuText" />
          </Tooltip>
        </Button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner style={{ position: "fixed", top: "60px", left: "100%", transform: "translateX(-420px)", zIndex: 1000 }}>
          <Popover.Content width="350px" height="calc(100vh - 360px)" overflowY="auto" overflowX="hidden">
            <Popover.Arrow />
            <Popover.Body p={2}>
              <HStack gap={2} mb={2} justify={"space-between"}>
                <HStack gap={2}>
                  <LuRotate3D size={"20px"} />
                  <Text fontWeight={"bold"} fontSize={"md"}>
                    Point Cloud Setting
                  </Text>
                </HStack>
                <Button bg={"transparent"} onClick={() => setIsOpenPointCloudPanel(false)}>
                  <LuX color="MenuText" />
                </Button>
              </HStack>

              <Separator mb={2} />
              <Text mb={2}>Mode</Text>
              <HStack gap={2}>
                <Checkbox.Root checked={pointCloudMode === "map"} onCheckedChange={(v) => v.checked && setPointCloudMode("map")}>
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>Map mode</Checkbox.Label>
                </Checkbox.Root>

                <Checkbox.Root checked={pointCloudMode === "object"} onCheckedChange={(v) => v.checked && setPointCloudMode("object")}>
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>Object mode</Checkbox.Label>
                </Checkbox.Root>
              </HStack>

              <FileUpload.Root
                mt={2}
                width={"full"}
                disabled={pointCloudMode === "map"}
                onFileChange={(v) => {
                  if (!v?.acceptedFiles?.length) return;
                  const file = v.acceptedFiles[0];
                  if (!file) return;
                  if (bgImageUrl && bgImageUrl.startsWith && bgImageUrl.startsWith("blob:")) {
                    try {
                      URL.revokeObjectURL(bgImageUrl);
                    } catch (_) {}
                  }
                  const imageUrl = URL.createObjectURL(file);
                  setBgImageUrl(imageUrl);
                  if (viewerRef?.current && pointCloudMode === "object") {
                    const scene = viewerRef.current.scene;
                    scene.backgroundColor = Color.TRANSPARENT;
                    scene.skyBox.show = false;
                    scene.skyAtmosphere.show = false;
                    scene.globe.show = false;
                  }
                }}
              >
                <FileUpload.HiddenInput accept="image/*" />
                <FileUpload.Trigger asChild>
                  <Button variant="outline" size="sm" width={"full"}>
                    Select Image
                  </Button>
                </FileUpload.Trigger>
              </FileUpload.Root>

              <Collapsible.Root>
                <Collapsible.Trigger paddingY="3" cursor={"pointer"}>
                  <HStack>
                    <LuPlus />
                    <Text>Point Style</Text>
                  </HStack>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Box padding="4" borderWidth="1px">
                    <ColorPicker.Root
                      gap={0}
                      defaultValue={parseColor("#ffffff")}
                      onValueChange={(v) => {
                        debounceSet((val) => setPointsColor(rgbaToHex(val)), v.valueAsString);
                      }}
                    >
                      <ColorPicker.HiddenInput />
                      <ColorPicker.Label>Points Color</ColorPicker.Label>
                      <ColorPicker.Control>
                        <ColorPicker.Input />
                        <ColorPicker.Trigger />
                      </ColorPicker.Control>
                      <Portal>
                        <ColorPicker.Positioner>
                          <ColorPicker.Content>
                            <ColorPicker.Area />
                            <HStack>
                              <ColorPicker.EyeDropper size="xs" variant="outline" />
                              <ColorPicker.Sliders />
                            </HStack>
                          </ColorPicker.Content>
                        </ColorPicker.Positioner>
                      </Portal>
                    </ColorPicker.Root>

                    <HStack justify={"space-between"} width={"full"} mt={2}>
                      <Text>Points Size</Text>
                      <Checkbox.Root
                        gap={1}
                        size={"sm"}
                        checked={isPointSizeDynamic}
                        onCheckedChange={(v) => {
                          setIsPointSizeDynamic(v.checked);
                        }}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Dynamic</Checkbox.Label>
                      </Checkbox.Root>
                    </HStack>

                    <Field.Root gap={0} width={"full"}>
                      <NumberInput.Root
                        width={"full"}
                        defaultValue={pointSize}
                        min={0.1}
                        step={1}
                        disabled={isPointSizeDynamic}
                        onValueChange={(v) => {
                          const n = parseFloat(v && v.valueAsNumber ? v.valueAsNumber : v);
                          debounceSet(() => {
                            setPointSize(n);
                          }, null);
                        }}
                      >
                        <NumberInput.Control />
                        <NumberInput.Input />
                      </NumberInput.Root>
                    </Field.Root>

                    <VStack width={"full"} mt={2} gap={0}>
                      <Text width={"full"} fontWeight={"bold"} textAlign={"left"}>
                        Point Shape
                      </Text>
                      <HStack width={"full"}>
                        <Checkbox.Root mt={2} gap={1} checked={pointShape === "square"} onCheckedChange={(v) => v.checked && setPointShape("square")}>
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label>Square</Checkbox.Label>
                        </Checkbox.Root>

                        <Checkbox.Root mt={2} gap={1} checked={pointShape === "circle"} onCheckedChange={(v) => v.checked && setPointShape("circle")}>
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label>Circle</Checkbox.Label>
                        </Checkbox.Root>
                      </HStack>
                    </VStack>
                  </Box>
                </Collapsible.Content>
              </Collapsible.Root>

              <Collapsible.Root>
                <Collapsible.Trigger paddingY="3" cursor={"pointer"}>
                  <HStack>
                    <LuPlus />
                    <Text>Return Number</Text>
                  </HStack>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Box padding="4" borderWidth="1px">
                    <Text>Return Number Weight </Text>
                    <Slider.Root
                      width="full"
                      mt={2}
                      min={0}
                      max={100}
                      step={1}
                      defaultValue={[returnNumberWeight]}
                      onValueChange={(val) => {
                        const [min] = val.value;
                        debounceSet((v) => setReturnNumberWeight(v), min);
                      }}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumb index={0} />
                      </Slider.Control>
                    </Slider.Root>

                    <Text mt={3}>
                      Visible Range: {returnNumberVisibleRange[0]} to {returnNumberVisibleRange[1]}{" "}
                    </Text>
                    <Slider.Root
                      width="full"
                      mt={2}
                      min={1}
                      max={5}
                      step={1}
                      defaultValue={returnNumberVisibleRange}
                      onValueChange={(val) => {
                        const [min, max] = val.value;
                        debounceSet((v) => setReturnNumberVisibleRange(v), [min, max]);
                      }}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumb index={0} />
                        <Slider.Thumb index={1} />
                      </Slider.Control>
                    </Slider.Root>
                  </Box>
                </Collapsible.Content>
              </Collapsible.Root>

              <Collapsible.Root>
                <Collapsible.Trigger paddingY="3" cursor={"pointer"}>
                  <HStack>
                    <LuPlus />
                    <Text> Intensity Setting</Text>
                  </HStack>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Box padding="4" borderWidth="1px">
                    <HStack justify={"space-between"}>
                      <Checkbox.Root mt={2} checked={isActiveIntensity} onCheckedChange={(v) => setIsActiveIntensity(v.checked)}>
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>Intensity [{intensityRange.join(",")}]</Checkbox.Label>
                      </Checkbox.Root>

                      <Checkbox.Root
                        mt={2}
                        gap={1}
                        checked={bit == 16}
                        disabled={!isActiveIntensity}
                        onCheckedChange={(v) => {
                          const newBit = v.checked ? 16 : 8;
                          setBit(newBit);
                          setIntensityRange(newBit === 16 ? [0, 65535] : [0, 255]);
                        }}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>16 bit</Checkbox.Label>
                      </Checkbox.Root>
                    </HStack>

                    <Slider.Root
                      disabled={!isActiveIntensity}
                      width="full"
                      mt={2}
                      min={0}
                      max={bit == 8 ? 255 : 65535}
                      step={1}
                      defaultValue={intensityRange}
                      onValueChange={(val) => {
                        const [min, max] = val.value;
                        debounceSet(() => setIntensityRange([min, max]), null);
                      }}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumb index={0} />
                        <Slider.Thumb index={1} />
                      </Slider.Control>
                    </Slider.Root>

                    <HStack justify={"space-between"}>
                      <HStack justify={"space-between"} mt={2}>
                        <Text>From</Text>
                        <ColorPicker.Root
                          onValueChange={(v) => {
                            debounceSet((val) => setIntensityColor((prev) => ({ ...prev, from: rgbaToHex(val) })), v.valueAsString);
                          }}
                          defaultValue={parseColor(intensityColor.from)}
                          maxW="200px"
                          size={"sm"}
                          disabled={!isActiveIntensity}
                        >
                          <ColorPicker.HiddenInput />
                          <ColorPicker.Control>
                            <ColorPicker.Trigger />
                          </ColorPicker.Control>
                          <Portal>
                            <ColorPicker.Positioner>
                              <ColorPicker.Content>
                                <ColorPicker.Area />
                                <HStack>
                                  <ColorPicker.EyeDropper size="xs" variant="outline" />
                                  <ColorPicker.Sliders />
                                </HStack>
                              </ColorPicker.Content>
                            </ColorPicker.Positioner>
                          </Portal>
                        </ColorPicker.Root>
                      </HStack>

                      <HStack justify={"space-between"} mt={2}>
                        <Text>To</Text>
                        <ColorPicker.Root
                          onValueChange={(v) => {
                            debounceSet((val) => setIntensityColor((prev) => ({ ...prev, to: rgbaToHex(val) })), v.valueAsString);
                          }}
                          defaultValue={parseColor(intensityColor.to)}
                          maxW="200px"
                          size={"sm"}
                          disabled={!isActiveIntensity}
                        >
                          <ColorPicker.HiddenInput />
                          <ColorPicker.Control>
                            <ColorPicker.Trigger />
                          </ColorPicker.Control>
                          <Portal>
                            <ColorPicker.Positioner>
                              <ColorPicker.Content>
                                <ColorPicker.Area />
                                <HStack>
                                  <ColorPicker.EyeDropper size="xs" variant="outline" />
                                  <ColorPicker.Sliders />
                                </HStack>
                              </ColorPicker.Content>
                            </ColorPicker.Positioner>
                          </Portal>
                        </ColorPicker.Root>
                      </HStack>
                    </HStack>
                  </Box>
                </Collapsible.Content>
              </Collapsible.Root>

              <Collapsible.Root>
                <Collapsible.Trigger paddingY="3" cursor={"pointer"}>
                  <HStack>
                    <LuPlus />
                    <Text>RGB Setting</Text>
                  </HStack>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Box padding="4" borderWidth="1px">
                    <Checkbox.Root mt={2} checked={isActiveRGB} onCheckedChange={(v) => setIsActiveRGB(v.checked)}>
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>RGB</Checkbox.Label>
                    </Checkbox.Root>
                    <Slider.Root
                      disabled={!isActiveRGB}
                      colorPalette={"red"}
                      width="full"
                      mt={2}
                      min={0}
                      max={65535}
                      step={1}
                      onValueChange={(val) => {
                        debounceSet(() => setRgbData((prev) => ({ ...prev, r: val.value })), null);
                      }}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumb />
                      </Slider.Control>
                    </Slider.Root>
                    <Slider.Root
                      disabled={!isActiveRGB}
                      colorPalette={"green"}
                      width="full"
                      mt={2}
                      min={0}
                      max={65535}
                      step={1}
                      onValueChange={(val) => {
                        debounceSet(() => setRgbData((prev) => ({ ...prev, g: val.value })), null);
                      }}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumb />
                      </Slider.Control>
                    </Slider.Root>
                    <Slider.Root
                      disabled={!isActiveRGB}
                      colorPalette={"blue"}
                      width="full"
                      mt={2}
                      min={0}
                      max={65535}
                      step={1}
                      onValueChange={(val) => {
                        debounceSet(() => setRgbData((prev) => ({ ...prev, b: val.value })), null);
                      }}
                    >
                      <Slider.Control>
                        <Slider.Track>
                          <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumb />
                      </Slider.Control>
                    </Slider.Root>
                  </Box>
                </Collapsible.Content>
              </Collapsible.Root>

              <Collapsible.Root>
                <Collapsible.Trigger paddingY="3" cursor={"pointer"}>
                  <HStack>
                    <LuPlus />
                    <Text>Classification</Text>
                  </HStack>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Box padding="4" borderWidth="1px">
                    <Checkbox.Root mt={2} checked={isActiveClassification} onCheckedChange={(v) => setIsActiveClassification(v.checked)}>
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>Classification</Checkbox.Label>
                    </Checkbox.Root>
                    {Object.entries(classificationData).map(([key, val]) => (
                      <HStack justify={"space-between"} mt={2} key={key}>
                        <Text style={{ textTransform: "capitalize" }}>{key.replace("_", " ")}</Text>
                        <ColorPicker.Root
                          onValueChange={(v) => setClassificationData((prev) => ({ ...prev, [key]: rgbaToHex(v.valueAsString) }))}
                          defaultValue={parseColor(val)}
                          maxW="200px"
                          size={"sm"}
                          disabled={!isActiveClassification}
                        >
                          <ColorPicker.HiddenInput />
                          <ColorPicker.Control>
                            <ColorPicker.Trigger />
                          </ColorPicker.Control>
                          <Portal>
                            <ColorPicker.Positioner>
                              <ColorPicker.Content>
                                <ColorPicker.Area />
                                <HStack>
                                  <ColorPicker.EyeDropper size="xs" variant="outline" />
                                  <ColorPicker.Sliders />
                                </HStack>
                              </ColorPicker.Content>
                            </ColorPicker.Positioner>
                          </Portal>
                        </ColorPicker.Root>
                      </HStack>
                    ))}
                  </Box>
                </Collapsible.Content>
              </Collapsible.Root>

              <Text mt={2}>Height Offset</Text>
              <HStack>
                <NumberInput.Root defaultValue={heightOffset} step={0.1} width={"full"} onChange={(v) => setHeightOffset(parseFloat(v.target.value))}>
                  <NumberInput.Control />
                  <NumberInput.Input />
                </NumberInput.Root>
                <Button
                  onClick={() => {
                    movePointCloudZRelative(getTileset(), heightOffset);
                  }}
                >
                  Done
                </Button>
              </HStack>

              <Text mt={2} fontSize="xs" color="gray.500">
                Offset: x={bgOffset.x}px, y={bgOffset.y}px (use arrow keys)
              </Text>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default PointCloud;
